'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import {
    Alignable,
    Button,
    ButtonVariant,
    Columns,
    Icon,
    IconName,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
    TypographyBold,
    TypographyColor,
    View,
} from '@bytel/trilogy-react';

import { CategoryPlanType, MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';

import { SSIBlock } from '@components/ssi-block';

import { MarketingsState } from '@hooks/use-marketings';

import { getCurrentPhone } from '@helpers/cart';
import { isComptant } from '@helpers/funding';
import { partials } from '@helpers/partials';

import { PlanTypeId } from '@constants/plan';

import LogoBigMobile from '@assets/logo_B.svg';
import LogoBig from '@assets/logo_BIG.svg';
import ToutatisLogo from '@assets/toutatis-big-logo.svg';
import { OfferCardContainer } from '@home-mobile/components';
import { LockedPlanValue, initBlockedPlans, sendViewMorePlansTracking } from '@home-mobile/helpers';

import { PhoneInCart } from './phone-in-cart';

type OfferCategoryListProps = {
    category: CategoryPlanType;
    canBePartiallyHidden?: boolean;
    openOfferDetails: (offer: MobilePlanType) => void;
    currentCategory: PlanTypeId;
    phoneInCart?: Product;
    onAddToCart: (selectedOffer: MobilePlanType, fundingKey?: string) => Promise<void>;
    isConnected: boolean;
    marketingDiscounts: MarketingsState;
    isSmallPriceCategory: boolean;
    smallPricePlanGencodes?: string[];
};

export function OfferCategoryListing({
    category,
    canBePartiallyHidden = false,
    openOfferDetails,
    currentCategory,
    phoneInCart,
    onAddToCart,
    isConnected,
    marketingDiscounts,
    isSmallPriceCategory,
    smallPricePlanGencodes,
}: OfferCategoryListProps) {
    const [isPartiallyHidden, setIsPartiallyHidden] = useState(canBePartiallyHidden);

    const currentPhone = getCurrentPhone(category.products?.[0]?.simulatedCart);
    const cashFunding = currentPhone?.details.fundings?.find((f) => isComptant(f.type));

    const handleShowMore = () => {
        setIsPartiallyHidden(!isPartiallyHidden);
        sendViewMorePlansTracking(!isPartiallyHidden);
    };

    const hasHatInLine = category.products.some((p) => Boolean(p.planFlag || p.isOwned));
    const hasBlockedPlan = category.products.some((p) => p.locked === LockedPlanValue.ONE);

    useEffect(() => {
        initBlockedPlans(category);
    }, []);

    return (
        <Row id={category.code}>
            {isSmallPriceCategory ? (
                <>
                    <View className='logo-big-container-desktop is-hidden-touch'>
                        <Image src={LogoBig} alt='' className='toutatis-logo' />
                    </View>
                    <View className='logo-big-container-mobile is-hidden-desktop'>
                        <Image src={LogoBigMobile} alt='' className='toutatis-logo' />
                    </View>
                    <div className='big-z-index'>
                        <Title
                            level={TitleLevels.THREE}
                            markup={TitleMarkup.H3}
                            typo={[
                                TypographyAlign.TEXT_CENTERED,
                                TypographyBold.TEXT_WEIGHT_SEMIBOLD,
                                TypographyColor.TEXT_INFO,
                            ]}
                            className='is-flex is-aligned-center is-justified-center gap-medium'
                        >
                            Les petits <Image src={ToutatisLogo} alt='Big' className='toutatis-logo' />
                        </Title>
                        <Text
                            level={TextLevels.ONE}
                            typo={[
                                TypographyAlign.TEXT_CENTERED,
                                TypographyColor.TEXT_INFO,
                                TypographyBold.TEXT_WEIGHT_SEMIBOLD,
                            ]}
                        >
                            Des petits prix pour les petits besoins&nbsp;!
                        </Text>
                        <Spacer size={SpacerSize.ONE} />
                    </div>
                </>
            ) : (
                <>
                    <Title
                        level={TitleLevels.THREE}
                        markup={TitleMarkup.H3}
                        typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                    >
                        {category?.subtitle}
                    </Title>
                </>
            )}
            <Rows>
                {currentCategory === PlanTypeId.SIMO && category.products[0] && (
                    <PhoneInCart
                        phone={phoneInCart}
                        isFloating
                        selectedFunding={cashFunding}
                        plan={category.products[0]}
                    />
                )}
                <Row>
                    <Columns
                        align={Alignable.ALIGNED_CENTER}
                        multiline
                        className={`is-justified-start-mobile gap-huger-mobile ${canBePartiallyHidden ? 'can-be-hidden is-relative' : ''} ${isPartiallyHidden ? 'partially-hidden' : ''}`}
                    >
                        {category.products.map((plan) => (
                            <OfferCardContainer
                                key={plan.gencode}
                                openOfferDetails={openOfferDetails}
                                phoneInCart={phoneInCart}
                                plan={plan}
                                onAddToCart={onAddToCart}
                                isConnected={isConnected}
                                marketingDiscounts={marketingDiscounts}
                                isSmallPrice={
                                    isSmallPriceCategory || smallPricePlanGencodes?.includes(plan.gencode) || false
                                }
                                hatInLine={hasHatInLine}
                                showBlockedPlanZone={hasBlockedPlan}
                            />
                        ))}
                    </Columns>
                </Row>
            </Rows>
            {isSmallPriceCategory && <SSIBlock tagId='kids-watch' url={partials.MARKETING.KIDS_WATCH} />}
            {canBePartiallyHidden && (
                <div className='has-text-centered'>
                    <Spacer size={SpacerSize.FIVE} />
                    <Button onClick={handleShowMore} variant={ButtonVariant.PRIMARY}>
                        <Icon name={isPartiallyHidden ? IconName.ARROW_DOWN : IconName.ARROW_UP} />
                        {isPartiallyHidden ? 'Voir tous les forfaits' : 'Voir moins'}
                    </Button>
                </div>
            )}
        </Row>
    );
}
